import { Component, OnChanges, Input } from "@angular/core";

import { APIResults } from "src/app/sharedinterfaces";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities } from "src/app/dvs-utilities";
declare const BrandVars;

@Component({
	selector: "app-rtb-coefficients",
	templateUrl: "./rtb-coefficients.component.html",
	styleUrls: ["./rtb-coefficients.component.scss"]
})
export class RtbCoefficientsComponent implements OnChanges {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;

	public operations: APIResults.Operation[];
	public vshafts?: VShaftWithPlot[];
	public defaultVshaft = undefined;
	public selectedVshaft = undefined;
	public shaftOrder = 1;
	public adjustments: number[][];
	public opBefore: APIResults.Operation;
	public opAfter: APIResults.Operation;

	public timelineOptions: Highcharts.Options;

	public coefficientText?: string;
	public errormessage?: string;

	constructor(private api: ApiService, private charting: ChartingService) { }

	// TODO: Bug: if a regular RTB plot (vibe tab) is still in roundtrip, when it comes back it can overwrite this one (coeff tab)
	async ngOnChanges() {
		this.vshafts = this.rotor.vshaft;
		const findDefault = this.vshafts.find(item => item.default);
		if (findDefault) {
			this.defaultVshaft = findDefault.name;
			this.selectedVshaft = findDefault;
		} else {
			this.defaultVshaft = this.vshafts[0].name;
			this.selectedVshaft = this.vshafts[0];
		}

		// Initialize empty adjustments - note must make sure rows are different arrays
		this.adjustments = Array(this.rotor.balancedata.numadjustmenttypes).fill([]).map(() => Array(this.rotor.balancedata.numblades).fill(0));
		await this.refreshOperations();
	}

	async refreshOperations() {
		this.operations = undefined;
		const operations = await this.api.GetOperations(this.asset.asset_id, 800, true);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.opBefore = selectedOp;
		}
		this.opAfter = this.opBefore;
		await this.updateBothPlots();
	}

	async updateBothPlots() {
		await Promise.all([
			this.updatePlot(),
			this.updateTimeline(),
		]);
	}

	async updatePlot() {
		this.errormessage = undefined;
		if (this.opBefore) {
			// Clear the plots
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				const [beforeRegimes, afterRegimes] = await Promise.all([
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id),
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opAfter.operation_id),
				]);
				const badops = [];
				if (beforeRegimes.vibe.length === 0) {
					badops.push("Before");
				}
				if (afterRegimes.vibe.length === 0) {
					badops.push("After");
				}
				if (badops.length > 0) {
					this.errormessage = "No data for " + badops.join(", ") + " operation";
					return;
				}
				const allOn = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
				const [beforeResults, afterResults] = await Promise.all([
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id, [], this.shaftOrder, allOn, false, true),
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, this.opAfter.operation_id, [], this.shaftOrder, allOn, false, true),
				]);
				if (this.vshafts) {
					for (const vshaft of this.vshafts) {
						const beforeComp = beforeResults.data[vshaft.shftid];
						const afterComp = afterResults.data[vshaft.shftid];
						if (beforeComp && afterComp) {
							const series: Highcharts.SeriesScatterOptions[] = [];
							for (const regimeKey of Object.keys(beforeComp.regimes)) {
								let regime = this.regimeConfigs[Number(regimeKey)];
								if (!regime) regime = { name: "Unknown regime " + regimeKey, default: false };
								const regimedata = beforeComp.regimes[regimeKey];
								const points: any[] = [];
								// points.push.apply(points, regimedata.data);
								// points.push({ x: regimedata.prediction[0], y: regimedata.prediction[1], name: "Prediction", marker: { fillColor: "#DBCD00", radius: 6 } });
								points.push({ x: DvsUtilities.AddDegreeOffset(regimedata.average[0], vshaft.rotate), y: regimedata.average[1] });
								series.push({ name: regime.name, type: "scatter", data: points });
							}
							for (const regimeKey of Object.keys(afterComp.regimes)) {
								let regime = this.regimeConfigs[Number(regimeKey)];
								if (!regime) regime = { name: "Unknown regime " + regimeKey, default: false };
								const regimedata = afterComp.regimes[regimeKey];
								const points: any[] = [];
								// points.push.apply(points, regimedata.data);
								// points.push({ x: regimedata.prediction[0], y: regimedata.prediction[1], name: "Prediction", marker: { fillColor: "#DBCD00", radius: 6 } });
								points.push({ x: DvsUtilities.AddDegreeOffset(regimedata.average[0], vshaft.rotate), y: regimedata.average[1] });
								series.push({ name: regime.name, type: "scatter", data: points, marker: { fillColor: "#DBCD00", radius: 5 } });
							}
							vshaft.plotOptions = this.charting.RTBScatterDefaults(series);
						} else {
							vshaft.plotOptions = undefined;
						}
					}
				}
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load RTB data";
				const remotemessage = DvsUtilities.GetRemoteErrorMessage(err);
				if (remotemessage) {
					this.errormessage += "\n\n" + remotemessage;
				}
			}
		}
	}

	async changeTab(event) {
		this.selectedVshaft = this.vshafts.find(item => item.name === event.nextId);
		await this.updateTimeline();
	}
	async updateTimeline() {
		this.timelineOptions = undefined;
		const [adjustments, history] = await Promise.all([
			this.api.GetAdjustmentHistory(this.asset.asset_id, this.rotor.rotorid),
			this.api.GetRTBHistory(this.asset.asset_id, this.rotor.rotorid, this.selectedVshaft.shftid, [], this.shaftOrder),
		]);
		const timelineSeries: Highcharts.SeriesLineOptions[] = [];
		for (const regimeData of history.regimes) {
			let regimeLookup = this.regimeConfigs[regimeData.regime];
			if (!regimeLookup) regimeLookup = { name: "Unknown regime " + regimeData.regime, default: false };
			const polarPoints: any[] = [];
			const timelinePoints: any[] = [];
			for (const op of regimeData.operations) {
				polarPoints.push({ x: op.phase, y: op.mag, time: op.ts });
				timelinePoints.push({ x: op.ts * 1000, y: op.mag });
			}
			timelineSeries.push({
				name: regimeLookup.name,
				type: "line",
				data: timelinePoints,
			});
		}
		this.timelineOptions = this.charting.RTBHistoryTimelineDefaults(timelineSeries, "Magnitude (IPS)", this.fleet ? this.fleet.time_offset_hours : 0, adjustments, undefined, this);
	}

	async CalculateCoefficients() {
		this.coefficientText = undefined;
		const result = await this.api.GetRTBCoefficients(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id, this.opAfter.operation_id, this.adjustments);
		this.coefficientText = result.data.join("\n");
	}

	public FilterBlades(displaygroup: string) {
		return this.rotor.blade.filter(item => item.displaygroup === displaygroup);
	}
}

interface VShaftWithPlot extends APIResults.VShaft {
	plotOptions?: Highcharts.Options;
}
